interface TrialTaxonomyMap {
  [taxonomy: string]: string[];
}

interface DataFilters {
  [key: string]: string[];
}

const trialScreener = (): void => {
  const selectors = {
    screener: '[data-screener]',
    screenerForm: '[data-screener-form]',
    screenerStep: 'data-screener-step',
    screenerApplyFilter: '[data-screener-filter-apply]',
    screenerStepControl: '[data-screener-step-control]',
    screenerStepControlAttr: 'data-screener-step-control',
    screenerReultsURL: 'data-screener-results-page',
    screenerTrialsData: '[data-screener-trials-data]',
    screenerFilterGroup: '[data-screener-filter-group]',
  };

  const screeners = document.querySelectorAll(selectors.screener);

  if (screeners) {
    screeners.forEach((screener) => {
      const allowMultipleOptions = true;

      const dataFilters: DataFilters = {};
      const screenerReultsURL = screener.getAttribute(
        selectors.screenerReultsURL
      );
      const screenerForm = screener.querySelector(selectors.screenerForm);

      // Load trials data for dynamic filtering
      const trialsDataEl = screener.querySelector(selectors.screenerTrialsData);
      const trialsData: TrialTaxonomyMap[] = trialsDataEl
        ? JSON.parse(trialsDataEl.textContent || '[]')
        : [];

      if (screenerForm) {
        const stepControls = screenerForm.querySelectorAll(
          selectors.screenerStepControl
        );

        const filters: NodeListOf<HTMLButtonElement> =
          screenerForm.querySelectorAll('[data-screener-filter]');

        const filtersApply = screenerForm.querySelector(
          selectors.screenerApplyFilter
        );

        // Count matching trials for a hypothetical selection
        // Only uses filters from OTHER taxonomies to check if this option has results
        const countMatchingTrials = (
          currentFilters: DataFilters,
          checkTaxonomy: string,
          checkValue: string
        ): number => {
          // Build test filters from OTHER taxonomies only (not the one we're checking)
          const testFilters: DataFilters = {};
          for (const [key, value] of Object.entries(currentFilters)) {
            if (key !== checkTaxonomy) {
              testFilters[key] = [...value];
            }
          }

          // Add only the option we're checking
          testFilters[checkTaxonomy] = [checkValue];

          return trialsData.filter((trial) => {
            // Trial matches if it satisfies ALL taxonomy constraints
            for (const [taxonomy, selectedValues] of Object.entries(
              testFilters
            )) {
              if (selectedValues.length === 0) continue;
              const trialValues = trial[taxonomy] || [];
              // Check if trial has at least one of the selected values (IN logic)
              const hasMatch = selectedValues.some((v) =>
                trialValues.includes(v)
              );
              if (!hasMatch) return false;
            }
            return true;
          }).length;
        };

        // Update disabled state of all options
        const updateOptionAvailability = (): void => {
          filters.forEach((button: HTMLButtonElement) => {
            const filterKey = button.getAttribute('data-screener-filter-key');
            const filterValue = button.getAttribute('data-screener-filter-value');

            // Skip "Unsure" button - always enabled
            if (filterValue === 'clear-filter') {
              button.removeAttribute('data-disabled');
              button.disabled = false;
              return;
            }

            // Check if this option is already selected
            const isSelected =
              filterKey &&
              filterValue &&
              dataFilters[filterKey]?.includes(filterValue);

            if (isSelected) {
              // Selected options stay enabled
              button.removeAttribute('data-disabled');
              button.disabled = false;
            } else if (filterKey && filterValue) {
              // Check if adding this option would yield results
              const matchCount = countMatchingTrials(
                dataFilters,
                filterKey,
                filterValue
              );

              if (matchCount === 0) {
                button.setAttribute('data-disabled', 'true');
                button.disabled = true;
              } else {
                button.removeAttribute('data-disabled');
                button.disabled = false;
              }
            }
          });
        };

        // Remove invalid selections when going back
        const validateSelections = (): void => {
          for (const [taxonomy, values] of Object.entries(dataFilters)) {
            const validValues = values.filter((value) => {
              // Check if this value is still valid given other taxonomy filters
              const matchCount = countMatchingTrials(
                dataFilters,
                taxonomy,
                value
              );
              return matchCount > 0;
            });

            // Update filters and UI if values were removed
            if (validValues.length !== values.length) {
              if (validValues.length === 0) {
                delete dataFilters[taxonomy];
              } else {
                dataFilters[taxonomy] = validValues;
              }

              // Update button states
              filters.forEach((btn: HTMLButtonElement) => {
                const btnKey = btn.getAttribute('data-screener-filter-key');
                const btnValue = btn.getAttribute('data-screener-filter-value');
                if (
                  btnKey === taxonomy &&
                  btnValue &&
                  !validValues.includes(btnValue)
                ) {
                  btn.removeAttribute('selected');
                }
              });
            }
          }
        };

        const loadPage = (): void => {
          let dataFiltersString = screenerReultsURL + '?';
          for (const [key, value] of Object.entries(dataFilters)) {
            dataFiltersString = dataFiltersString + key + '=' + value + '&';
          }
          dataFiltersString = dataFiltersString.substring(
            0,
            dataFiltersString.length - 1
          );
          window.location.href = dataFiltersString;
        };

        const applyFilters = (filterKey: string, filterValue: string): void => {
          if (allowMultipleOptions) {
            if (dataFilters[filterKey]) {
              const prevOptions = dataFilters[filterKey];
              prevOptions.push(filterValue);
              dataFilters[filterKey] = prevOptions;
            } else {
              const options = [filterValue];
              dataFilters[filterKey] = options;
            }
          } else {
            dataFilters[filterKey] = [filterValue];
          }
        };

        const clearFilter = (filterKey: string, filterValue: string): void => {
          if (allowMultipleOptions) {
            if (dataFilters[filterKey]) {
              if (
                dataFilters[filterKey].length == 1 &&
                dataFilters[filterKey][0] == filterValue
              ) {
                delete dataFilters[filterKey];
              } else {
                dataFilters[filterKey] = dataFilters[filterKey].filter(
                  (item: string) => item !== filterValue
                );
              }
            }
          } else {
            if (dataFilters[filterKey]) {
              delete dataFilters[filterKey];
            }
          }
        };

        filters.forEach((filter: HTMLButtonElement) => {
          filter.addEventListener('click', () => {
            // Skip if disabled
            if (filter.disabled || filter.hasAttribute('data-disabled')) {
              return;
            }

            const filterKey = filter.getAttribute('data-screener-filter-key');
            const filterValue = filter.getAttribute(
              'data-screener-filter-value'
            );
            const filterStatus = filter.getAttribute('selected');

            if (!filterKey || !filterValue) return;

            if (!allowMultipleOptions) {
              filters.forEach((otherFilter: HTMLButtonElement) => {
                if (
                  otherFilter.getAttribute('data-screener-filter-key') ==
                  filter.getAttribute('data-screener-filter-key')
                ) {
                  otherFilter.removeAttribute('selected');
                }
              });
            }

            if (filterStatus == 'selected' || filterValue == 'clear-filter') {
              if (filterValue == 'clear-filter') {
                delete dataFilters[filterKey];

                filters.forEach((otherFilter: HTMLButtonElement) => {
                  if (
                    otherFilter.getAttribute('data-screener-filter-key') ==
                    filter.getAttribute('data-screener-filter-key')
                  ) {
                    otherFilter.removeAttribute('selected');
                  }
                });
                filter.setAttribute('selected', 'selected');
              } else {
                filter.removeAttribute('selected');

                clearFilter(filterKey, filterValue);
              }
            } else {
              applyFilters(filterKey, filterValue);
              filter.setAttribute('selected', 'selected');
            }

            // Update availability after any selection change
            updateOptionAvailability();
          });
        });

        stepControls.forEach((control) => {
          control.addEventListener('click', () => {
            const step = control.getAttribute(
              selectors.screenerStepControlAttr
            );
            const currentStep = parseInt(
              screenerForm.getAttribute(selectors.screenerStep) || '1'
            );
            const newStep = parseInt(step || '1');

            // Going backwards - validate later selections
            if (newStep < currentStep) {
              validateSelections();
            }

            screenerForm.setAttribute(selectors.screenerStep, step || '1');

            // Update availability for the new step
            updateOptionAvailability();
          });
        });

        if (filtersApply) {
          filtersApply.addEventListener('click', loadPage);
        }

        // Initial availability check
        updateOptionAvailability();
      }
    });
  }
};

export default trialScreener;
